import { useSafeTimeout } from '@mm/hooks';
import { useOnScroll } from '@mm/hooks/src/use-on-scroll';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import classNames from 'classnames';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INTEGRATIONS_SECTIONS, PageSettingDefinition } from '../constants';
import styles from './IntegrationsPanel.module.css';
import { canUseFacebookAdsSyncIntegrationAndWidget } from '@mm/state/selectors/adAccountsSelecotrs';
import { useSelector } from 'react-redux';
import { selectIsShopifyContext } from '@mm/state/selectors/viewSelectors';

export interface IntegrationSectionProps {
  section: PageSettingDefinition;
}

export type IntegrationLinkType =
  | 'email'
  | 'native-integrations'
  | 'contact-suppression'
  | 'notifications'
  | 'facebook'
  | 'xray';

export const IntegrationsPanel = () => {
  const canUseFacebookAds = useSelector(
    canUseFacebookAdsSyncIntegrationAndWidget,
  );

  const [activeLink, setActiveLink] = useState<IntegrationLinkType | undefined>(
    INTEGRATIONS_SECTIONS[0].id,
  );
  const { invalidateAll } = useConnectionCRUDActions();
  const integrationsContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollManually, setScrollManually] = useState(true);
  const { safeTimeout } = useSafeTimeout();
  const history = useHistory();
  const location = useLocation<{ kind: IntegrationLinkType }>();
  const locationKindParam = location.state?.kind;

  const getVisibleIds = () =>
    INTEGRATIONS_SECTIONS.map(({ id }) => {
      const el = document.getElementById(id);

      if (el) {
        const rect = el.getBoundingClientRect();
        const top = rect.y;
        const [max] = [window.innerHeight * 0.5];

        return top < max ? id : undefined;
      }

      return undefined;
    }).filter(Boolean);

  const onPageScroll = () => {
    if (scrollManually) {
      const visibleIds = getVisibleIds();
      const lastElementId = visibleIds?.[visibleIds.length - 1];
      setActiveLink(lastElementId);

      if (lastElementId) {
        const el: HTMLElement | null = document.querySelector(
          `[href='#${lastElementId}']`,
        );
        if (el) {
          el?.focus();
        }
      }
    }
  };

  useOnScroll(integrationsContainerRef.current, onPageScroll);

  const scrollToElement = (selector: IntegrationLinkType) => {
    setActiveLink(selector);
    document.getElementById(selector)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const onLinkClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault();
    setScrollManually(false);
    const el = e.target as Element;
    const href = el?.getAttribute('href');
    if (href) {
      const withoutHash = href.split('#')[1] as IntegrationLinkType;
      scrollToElement(withoutHash);
      safeTimeout(() => setScrollManually(true), 1000);
    }
  };

  const isShopify = useSelector(selectIsShopifyContext);

  const sectionsToRender = useMemo(
    () =>
      INTEGRATIONS_SECTIONS.filter(v =>
        isShopify ? !['email', 'facebook'].includes(v.id) : true,
      ),
    [isShopify],
  );

  useLayoutEffect(() => {
    if (isShopify) {
      setScrollManually(false);
      scrollToElement('native-integrations');
      history.replace({ state: {} });
      return;
    }

    if (
      locationKindParam &&
      INTEGRATIONS_SECTIONS.find(section => section.id === locationKindParam)
    ) {
      setScrollManually(false);
      scrollToElement(locationKindParam);
      history.replace({ state: {} });
    }
    return () => {
      setActiveLink(undefined);
      invalidateAll();
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>Integrations</div>
      <div className={styles.content}>
        <div className={styles.navigationContainer}>
          {sectionsToRender.map(section => {
            const { id } = section;

            if (id === 'facebook' && !canUseFacebookAds) {
              return <></>;
            }
            return (
              <a
                key={id}
                className={classNames(styles.hashLink, {
                  [styles.activeHashLink]: activeLink === id,
                })}
                onClick={onLinkClick}
                href={`#${id}`}
              >
                {section.name}
              </a>
            );
          })}
        </div>
        <div
          className={styles.integrationsContainer}
          ref={integrationsContainerRef}
        >
          {sectionsToRender.map(section => {
            const Component: any = section.component;

            if (section.id === 'facebook' && !canUseFacebookAds) {
              return <></>;
            }

            return (
              <section id={section.id} key={section.id}>
                <div>
                  <div className={styles.titleContainer}>
                    <div className={styles.title}>{section.name}</div>
                    {section.helpDoc && (
                      <a
                        href={section.helpDoc}
                        className={styles.help}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Help Center
                      </a>
                    )}
                  </div>
                  <div>{section.subtitle}</div>
                </div>
                <Component section={section} />
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};
